export const getTokenExpiryTimeMs = (
  token: string | null | undefined,
): number | null => {
  const payload = token?.split('.')[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(base64)) as { exp?: unknown };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// NOTE: An unreadable token is not "expiring": send it and let the server decide.
export const isTokenExpiring = (
  token: string | null | undefined,
  now: number = Date.now(),
  skewSeconds: number = 90,
): boolean => {
  const expiryTimeMs = getTokenExpiryTimeMs(token);
  if (expiryTimeMs === null) return false;
  return expiryTimeMs - skewSeconds * 1000 <= now;
};

type SessionDeps = {
  refreshUrl: string;
  fetchFn: typeof fetch;
};

export const createSessionRefresher = ({
  refreshUrl,
  fetchFn,
  onToken,
}: SessionDeps & { onToken: (token: string) => void }): {
  refresh: () => Promise<string | null>;
} => {
  let inFlight: Promise<string | null> | null = null;

  const doRefresh = async (): Promise<string | null> => {
    const response = await fetchFn(refreshUrl, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) return null;
    if (!response.ok) {
      throw new Error(`Session refresh failed with status ${response.status}`);
    }

    const { token } = (await response.json()) as { token?: string };
    if (!token) return null;

    onToken(token);
    return token;
  };

  return {
    // NOTE: Single-flight. Refresh tokens rotate, so parallel refreshes would replay the same cookie.
    refresh: () => {
      inFlight ??= doRefresh().finally(() => {
        inFlight = null;
      });
      return inFlight;
    },
  };
};

export const endServerSession = async ({
  refreshUrl,
  fetchFn,
}: SessionDeps): Promise<void> => {
  try {
    await fetchFn(refreshUrl, {
      method: 'DELETE',
      credentials: 'include',
      keepalive: true,
    });
  } catch {
    // NOTE: Logout must finish locally even if the server is unreachable.
  }
};

export const createOnceNotifier = (
  notify: () => void,
): { notify: () => void; reset: () => void } => {
  let notified = false;

  return {
    notify: () => {
      if (notified) return;
      notified = true;
      notify();
    },
    reset: () => {
      notified = false;
    },
  };
};

export const SESSION_EXPIRES_AT_KEY = 'sessionExpiresAt';
export const SESSION_WARNING_ACK_KEY = 'sessionWarningAcknowledged';

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

// NOTE: Read from storage instead of the store so a tab sees the value another tab wrote when the nurse re-authenticated there.
export const readSessionExpiresAt = (
  storage: KeyValueStorage,
): string | null => {
  const raw = storage.getItem(SESSION_EXPIRES_AT_KEY);
  if (!raw) return null;

  try {
    const value: unknown = JSON.parse(raw);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
};

export const getSessionTimeLeftMs = (
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): number | null => {
  if (!expiresAt) return null;
  const expiresAtMs = new Date(expiresAt).getTime();
  return Number.isNaN(expiresAtMs) ? null : expiresAtMs - now;
};

// NOTE: A session that is already over is not "ending": the logout is on its way and a warning would be wrong.
export const isSessionEnding = (
  timeLeftMs: number | null,
  warnBeforeMs: number,
): boolean =>
  timeLeftMs !== null && timeLeftMs > 0 && timeLeftMs <= warnBeforeMs;

// NOTE: The acknowledgement is keyed by the session end, so a new session (new end) is warned about again without any reset.
export const isSessionWarningAcknowledged = (
  storage: KeyValueStorage,
  expiresAt: string,
): boolean => storage.getItem(SESSION_WARNING_ACK_KEY) === expiresAt;

export const acknowledgeSessionWarning = (
  storage: KeyValueStorage,
  expiresAt: string,
): void => storage.setItem(SESSION_WARNING_ACK_KEY, expiresAt);

export const formatTimeLeft = (timeLeftMs: number): string => {
  const minutes = Math.ceil(timeLeftMs / 60_000);
  if (timeLeftMs < 60_000) return 'mindre än en minut';
  return minutes === 1 ? '1 minut' : `${minutes} minuter`;
};

export const TOKEN_KEY = 'token';

export const readStoredToken = (storage: KeyValueStorage): string | null => {
  const raw = storage.getItem(TOKEN_KEY);
  if (!raw) return null;

  try {
    const value: unknown = JSON.parse(raw);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
};

export type SessionPhase = 'active' | 'ending' | 'expired' | 'over';

/**
 * - active: nothing to say
 * - ending: inside the warning window before the maximum session length
 * - expired: the session is over on the server (no refresh can succeed) but the
 *   access token still works, so the nurse can re-authenticate until it runs out
 * - over: nothing left to save, the logout is happening
 */
export const getSessionPhase = ({
  sessionTimeLeftMs,
  tokenTimeLeftMs,
  warnBeforeMs,
}: {
  sessionTimeLeftMs: number | null;
  tokenTimeLeftMs: number | null;
  warnBeforeMs: number;
}): SessionPhase => {
  if (sessionTimeLeftMs === null || sessionTimeLeftMs > warnBeforeMs) {
    return 'active';
  }
  if (sessionTimeLeftMs > 0) return 'ending';
  return tokenTimeLeftMs !== null && tokenTimeLeftMs > 0 ? 'expired' : 'over';
};
